import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { events } from "@/mocks/events";
import { formatCount, formatDate } from "@/utils/format";
import CommentSection from "./components/CommentSection";
import EventSidebar from "./components/EventSidebar";
import RelatedEvents from "./components/RelatedEvents";

export default function EventDetail() {
  const { id } = useParams();
  const event = events.find((e) => e.id === id);

  usePageMeta({
    title: event
      ? `${event.title} · 热门吃瓜网友热评现场`
      : "事件未找到 · 瓜田热评",
    description: event
      ? `${event.summary} 查看该事件的网友热评、高赞评论与神回复整理。`
      : "抱歉，这个围观现场暂时找不到了，去瓜田广场看看其他热门吃瓜事件吧。",
    keywords: event
      ? `热门吃瓜,${event.categoryName},${event.tags.join(",")},热门吃瓜评论区,网友热评,高赞神回复`
      : "热门吃瓜,瓜田广场,网友热评",
    path: `/event/${id ?? ""}`,
    ogType: "article",
    jsonLd: event
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: event.title,
          description: event.summary,
          image: event.cover,
          datePublished: event.publishedAt,
          dateModified: event.publishedAt,
          inLanguage: "zh-CN",
          author: { "@type": "Organization", name: event.author },
          publisher: { "@type": "Organization", name: "瓜田热评" },
          articleSection: event.categoryName,
          keywords: event.tags.join(","),
          mainEntity: {
            "@type": "ItemList",
            name: "事件评论区高赞热评",
            itemListElement: event.comments.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `${c.nickname}：${c.content}`,
            })),
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "瓜田广场", item: "/events" },
              { "@type": "ListItem", position: 3, name: event.title, item: `/event/${event.id}` },
            ],
          },
        }
      : undefined,
  });

  if (!event) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center px-4 py-24 text-center md:py-32">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-4xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">这个瓜翻遍了瓜田也没找到</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            事件可能已经被整理下架，或者链接输入有误。去瓜田广场看看其他正在围观的现场吧。
          </p>
          <Link
            to="/events"
            className="mt-6 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line mr-1.5 text-base"></i>
            返回瓜田广场
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full px-4 py-8 md:px-8 md:py-12">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "瓜田广场", to: "/events" },
            { label: event.categoryName, to: `/events?cat=${event.category}` },
            { label: event.title },
          ]}
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <article className="lg:col-span-8">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/events?cat=${event.category}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {event.categoryName}
                </Link>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-fire-line text-sm text-primary-500"></i>
                  热度 {formatCount(event.heat)}
                </span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-500">{formatDate(event.publishedAt)} 更新</span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
                {event.title}
              </h1>

              <p className="mt-4 rounded-md border-l-4 border-primary-400 bg-primary-50 px-4 py-3 text-sm leading-relaxed text-foreground-800 md:text-base">
                {event.summary}
              </p>
            </header>

            <figure className="mt-6">
              <div className="h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-96">
                <img
                  src={event.cover}
                  alt={`${event.title} 事件现场配图`}
                  title={`${event.title} - 热门吃瓜网友热评`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2.5 text-center text-xs text-foreground-500">
                {event.title} · 示意图，仅用于呈现事件氛围
              </figcaption>
            </figure>

            <div className="mt-8 flex flex-col gap-5">
              {event.content.map((paragraph, index) => (
                <p key={`${event.id}-p-${index}`} className="text-[15px] leading-relaxed text-foreground-800 md:text-base md:leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-foreground-500">相关标签：</span>
              {event.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/events?q=${encodeURIComponent(tag)}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-200 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-price-tag-3-line text-sm"></i>
                  {tag}
                </Link>
              ))}
            </div>

            <CommentSection comments={event.comments} />
            <RelatedEvents current={event} />
          </article>

          <div className="lg:col-span-4">
            <EventSidebar event={event} />
          </div>
        </div>
      </div>
    </Layout>
  );
}